import {
  getDepositInstructionsCopy,
  getTransferMethodDetail,
} from './accountsHelpers';
import { getOnRampAccountFields, getPaymentMethodLabel } from './onRampHelpers';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatGeneratedAt(dateInput = new Date()) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatAddress(address) {
  if (!address) return null;
  if (typeof address === 'string') return address.trim() || null;
  if (typeof address === 'object') {
    const parts = [
      address.line1,
      address.line2,
      [address.postCode, address.city].filter(Boolean).join(' '),
      address.state,
      address.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  return null;
}

function collectExtraBankFields(bankAccount, knownLabels) {
  if (!bankAccount || typeof bankAccount !== 'object') return [];

  const skipKeys = new Set([
    'id',
    'type',
    'currency',
    'onRampProvider',
    'OnRampProvider',
    'accountHolderName',
    'beneficiaryName',
    'name',
    'iban',
    'accountNumber',
    'routingNumber',
    'swiftCode',
    'bic',
    'bankName',
    'accountType',
    'sortCode',
    'address',
    'bankAddress',
    'beneficiaryAddress',
  ]);

  const extras = [];
  Object.keys(bankAccount).forEach((key) => {
    if (skipKeys.has(key)) return;
    if (/provider/i.test(key)) return;
    const value = bankAccount[key];
    if (value == null || value === '') return;
    if (typeof value === 'string' && /^(quantoz|noah)$/i.test(value.trim())) return;
    if (typeof value === 'object') {
      const formatted = formatAddress(value);
      if (!formatted) return;
      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
      if (knownLabels.has(label.toLowerCase())) return;
      extras.push({ label, value: formatted });
      return;
    }
    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
    if (knownLabels.has(label.toLowerCase())) return;
    extras.push({ label, value: String(value) });
  });
  return extras;
}

function buildDetailRows(fields) {
  return fields
    .filter((field) => field?.value != null && String(field.value).trim() !== '')
    .map(
      (field) => `
      <tr>
        <th scope="row">${escapeHtml(field.label)}</th>
        <td>${escapeHtml(field.value)}</td>
      </tr>`,
    )
    .join('');
}

function buildRailSection(rail, currency, index) {
  const bankAccount = rail?.bankAccount || null;
  const methodLabel = getPaymentMethodLabel(bankAccount, currency);
  const fields = getOnRampAccountFields(bankAccount);
  const knownLabels = new Set(fields.map((field) => String(field.label || '').toLowerCase()));
  const extras = collectExtraBankFields(bankAccount, knownLabels);
  const allFields = [...fields, ...extras];
  const hint = getTransferMethodDetail(bankAccount)?.hint;
  const rows = buildDetailRows(allFields);

  return `
    <section class="rail ${index > 0 ? 'rail-spaced' : ''}">
      <div class="rail-header">
        <div>
          <h2 class="section-title">${escapeHtml(rail.shortLabel || rail.type || 'Bank')}</h2>
          <p class="rail-method">${escapeHtml(methodLabel || 'Bank transfer')}</p>
        </div>
      </div>
      ${
        rows
          ? `<table class="details"><tbody>${rows}</tbody></table>`
          : `<p class="empty">No deposit details available for this pay-in method.</p>`
      }
      ${hint ? `<p class="hint">${escapeHtml(hint)}</p>` : ''}
    </section>`;
}

export function buildAccountDetailsHtml(account) {
  const generatedAt = formatGeneratedAt(new Date());
  const title = account?.title || `${account?.currency || ''} Account`;
  const currency = account?.currency || '';
  const rails = Array.isArray(account?.rails) ? account.rails : [];
  const depositCopy = getDepositInstructionsCopy(rails[0]?.bankAccount, currency, {
    railsCount: rails.length || 1,
  });

  const railSections =
    rails.length > 0
      ? rails.map((rail, index) => buildRailSection(rail, currency, index)).join('')
      : `<p class="empty">No deposit details are available for this account yet.</p>`;

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)} · Account details</title>
    <style>
      @page { margin: 16mm 14mm; size: A4; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        padding: 24px;
        color: #0b1929;
        background: #eef3f8;
        font-family: "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 13px;
        line-height: 1.45;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .toolbar {
        max-width: 760px;
        margin: 0 auto 16px;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
      }
      .toolbar button {
        border: 1px solid rgba(46, 107, 173, 0.28);
        background: #fff;
        color: #0b1929;
        border-radius: 999px;
        padding: 10px 16px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
      }
      .toolbar button.primary {
        background: #0b1929;
        border-color: #0b1929;
        color: #fff;
      }
      .sheet {
        max-width: 760px;
        margin: 0 auto;
        background: #fff;
        border: 1px solid rgba(11, 25, 41, 0.08);
        border-radius: 16px;
        padding: 36px 40px;
        box-shadow: 0 8px 28px rgba(11, 25, 41, 0.06);
      }
      .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 24px;
        padding-bottom: 22px;
        border-bottom: 2px solid #0b1929;
        margin-bottom: 24px;
      }
      .brand {
        font-family: Georgia, "Times New Roman", serif;
        font-size: 28px;
        font-weight: 400;
        letter-spacing: -0.02em;
        margin: 0;
      }
      .brand-sub {
        margin: 4px 0 0;
        font-size: 11px;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: #64748b;
      }
      .meta {
        text-align: right;
        font-size: 12px;
        color: #475569;
      }
      .meta strong {
        display: block;
        color: #0b1929;
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 4px;
      }
      .summary {
        display: grid;
        grid-template-columns: 1.4fr 1fr;
        gap: 16px;
        margin-bottom: 28px;
      }
      .summary-card {
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 18px 20px;
        background: #f8fafc;
      }
      .summary-card.primary {
        background: #0b1929;
        border-color: #0b1929;
        color: #fff;
      }
      .summary-label {
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: #64748b;
        margin-bottom: 8px;
      }
      .summary-card.primary .summary-label {
        color: rgba(255, 255, 255, 0.62);
      }
      .summary-value {
        font-family: Georgia, "Times New Roman", serif;
        font-size: 28px;
        font-weight: 400;
        letter-spacing: -0.02em;
        line-height: 1.15;
      }
      .summary-sub {
        margin-top: 8px;
        font-size: 12.5px;
        color: #64748b;
      }
      .summary-card.primary .summary-sub {
        color: rgba(255, 255, 255, 0.7);
      }
      .intro {
        margin: 0 0 24px;
        color: #3d5a73;
        font-size: 13.5px;
        line-height: 1.55;
      }
      .section-title {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: #2e6bad;
        margin: 0 0 6px;
      }
      .rail-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        margin-bottom: 12px;
      }
      .rail-method {
        margin: 0;
        color: #3d5a73;
        font-size: 13px;
      }
      .rail-spaced {
        margin-top: 28px;
        padding-top: 24px;
        border-top: 1px solid #e8edf2;
      }
      table.details {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 10px;
      }
      table.details th,
      table.details td {
        padding: 11px 0;
        border-bottom: 1px solid #e8edf2;
        text-align: left;
        vertical-align: top;
      }
      table.details th {
        width: 36%;
        font-size: 12px;
        font-weight: 500;
        color: #64748b;
      }
      table.details td {
        font-size: 13px;
        font-weight: 600;
        color: #0b1929;
        word-break: break-word;
        font-variant-numeric: tabular-nums;
      }
      .hint {
        margin: 8px 0 0;
        color: #2e6bad;
        font-size: 12.5px;
      }
      .empty {
        margin: 0;
        color: #64748b;
        font-size: 13px;
      }
      .footer {
        margin-top: 32px;
        padding-top: 16px;
        border-top: 1px solid #e2e8f0;
        font-size: 11px;
        color: #94a3b8;
        line-height: 1.55;
      }
      .footer strong { color: #64748b; }
      @media print {
        body { background: #fff; padding: 0; }
        .toolbar { display: none !important; }
        .sheet {
          max-width: none;
          border: none;
          border-radius: 0;
          box-shadow: none;
          padding: 0;
        }
      }
    </style>
  </head>
  <body>
    <div class="toolbar">
      <button type="button" onclick="window.print()">Print / Save as PDF</button>
    </div>
    <div class="sheet">
      <header class="header">
        <div>
          <h1 class="brand">HansePay</h1>
          <p class="brand-sub">Account details</p>
        </div>
        <div class="meta">
          <strong>${escapeHtml(title)}</strong>
          <div>Generated ${escapeHtml(generatedAt)}</div>
        </div>
      </header>

      <div class="summary">
        <div class="summary-card">
          <div class="summary-label">Account</div>
          <div class="summary-value">${escapeHtml(title)}</div>
          <div class="summary-sub">
            ${escapeHtml(currency || '—')}
            ${account?.identifier ? ` · ${escapeHtml(account.identifier)}` : ''}
          </div>
        </div>
        <div class="summary-card primary">
          <div class="summary-label">Available balance</div>
          <div class="summary-value">${escapeHtml(account?.amountLabel || '—')}</div>
          <div class="summary-sub">${escapeHtml(currency || '')}</div>
        </div>
      </div>

      <p class="intro">${escapeHtml(depositCopy)}</p>

      ${railSections}

      <footer class="footer">
        <strong>Important</strong><br />
        These are your HansePay deposit details for the selected account. Always verify account numbers before sending funds.
        Arrival times depend on your bank and transfer method. This document is for your records and is not a bank guarantee of settlement timing.
      </footer>
    </div>
  </body>
</html>`;
}

export function openAccountDetailsPdf(account) {
  if (!account) {
    throw new Error('Account details are not available.');
  }

  const html = buildAccountDetailsHtml(account);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const blobUrl = URL.createObjectURL(blob);
  const printWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');

  if (!printWindow) {
    URL.revokeObjectURL(blobUrl);
    throw new Error('Could not open account details. Please allow popups for this site.');
  }

  // Revoke after the new tab has had time to load the blob.
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
